import React, { useState, useEffect } from "react";

// Redux
import { Link, useLocation } from "react-router-dom";
import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Form,
  FormFeedback,
} from "reactstrap";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// import images
import logo from "../../assets/images/logo/pappermap-logo-wordmark.png";

import withRouter from "Components/Common/withRouter";
import { BaseButton, BaseInput } from "Components/ui";
import { resetPassword } from "services/auth.service";

import { handleAPIError } from "utils";
import { toast } from "react-toastify";

const ChangePassword = (props: any) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);

  // Get the token from the URL
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const extractedToken = queryParams.get("token");
    if (extractedToken) {
      setToken(extractedToken);
    }
    console.log("Token: ", extractedToken);
  }, [location.search]);

  //meta title
  document.title = "Change Password | Papermap";

  // Form validation
  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirmPassword: "",
      token: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords do not match")
        .required("Confirm Password is required"),
    }),
    onSubmit: (values: any) => {
      onSubmit(values);
    },
  });

  const onSubmit = async (data: any) => {
    if (!token) {
      toast.error("No token found.");
      return;
    }
    data.token = token;
    setLoading(true);
    try {
      const result = await resetPassword({
        password: data.password,
        token: data.token,
      });
      console.log("Result: ", result);
      validation.resetForm();
    } catch (error) {
      handleAPIError(error, true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">Welcome Back!</h5>
                        <p>
                          Change your password to continue to{" "}
                          <span className="font-weight-bold">Papermap</span>.
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div className="auth-logo">
                    <Link to="/" className="auth-logo-light">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            src={logo}
                            alt=""
                            className="rounded-circle"
                            height="34"
                          />
                        </span>
                      </div>
                    </Link>
                    <Link to="/" className="auth-logo-dark">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-dark">
                          <img
                            src={logo}
                            alt=""
                            className="rounded-circle"
                            height="54"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-3">
                        <BaseInput
                          id="password"
                          name="password"
                          label="New Password"
                          value={validation.values.password || ""}
                          type="password"
                          placeholder="Enter Password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.password &&
                              validation.errors.password
                          )}
                          errorMessage={validation.errors.password}
                        />
                        {validation.touched.password &&
                        validation.errors.password ? (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <BaseInput
                          id="confirmPassword"
                          name="confirmPassword"
                          label="Confirm Password"
                          value={validation.values.confirmPassword || ""}
                          type="password"
                          placeholder="Confirm new password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.confirmPassword &&
                              validation.errors.confirmPassword
                          )}
                          errorMessage={validation.errors.confirmPassword}
                        />
                        {validation.touched.confirmPassword &&
                        validation.errors.confirmPassword ? (
                          <FormFeedback type="invalid">
                            {validation.errors.confirmPassword}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mt-3 d-grid">
                        <BaseButton
                          className="btn btn-primary btn-block"
                          type="submit"
                          loading={loading}
                          disabled={loading}
                        >
                          Change Password
                        </BaseButton>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  Remember your password now?{" "}
                  <Link to="/login" className="fw-medium text-primary">
                    {" "}
                    Go back to Login{" "}
                  </Link>{" "}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(ChangePassword);
